/*
 * Copyright © HatioLab Inc. All rights reserved.
 *
 * Stocker (AS/RS) Component
 *
 * Configurable automated storage/retrieval system:
 * - Independent L/R rack configuration (bays, levels, depth)
 * - Freely positioned In/Out ports
 * - Central aisle with crane (2-axis movement)
 */

import { Component, ComponentNature, RealObject, RectPath, Shape, sceneComponent } from '@hatiolab/things-scene'
import type { State } from '@hatiolab/things-scene'
import { Stocker3d } from './stocker-3d.js'

/** Stocker 컴포넌트 state */
export interface StockerState extends State {
  lBays?: number
  lLevels?: number
  lDepth?: number
  rBays?: number
  rLevels?: number
  rDepth?: number
  cranes?: number
  frontSide?: FrontSide
  hideRackFrame?: boolean
  aisleRatio?: number
  locationRule?: string | LocationRule
}

// ── Types ──

export interface RackConfig {
  bays: number
  levels: number
  depthCount: number
}

export interface LocationRule {
  pattern: string            // '{side}{bay}-{level}-{depth}'
  sideLabels: { L: string; R: string }  // { L: 'A', R: 'B' }
  bayDir: 'ltr' | 'rtl'     // 번호 방향
  levelDir: 'btt' | 'ttb'   // 번호 방향 (bottom-to-top / top-to-bottom)
  bayStart: number           // 시작 번호
  levelStart: number
  depthStart: number
  bayDigits: number          // 자릿수 (0=패딩 없음)
  levelDigits: number
  depthDigits: number
}

export interface CraneData {
  id?: string
  bay: number
  level: number
  side?: 'L' | 'R'
  status?: string  // IDLE, MOVING, PICKING, PLACING
  carrier?: string
}

export interface StockerCell {
  side: 'L' | 'R'
  bay: number
  level: number
  depth: number
  carrier?: string
  status?: string // FULL, EMPTY, RESERVED, ERROR, ...
}

export interface StockerData {
  STATUS?: string
  // 단일 crane (하위 호환)
  CRANE_BAY?: number
  CRANE_LEVEL?: number
  CRANE_SIDE?: 'L' | 'R'
  CRANE_STATUS?: string
  CRANE_CARRIER?: string
  // 복수 crane
  CRANES?: string | CraneData[]
  CELLS?: string | StockerCell[]
  TOTAL_CAPACITY?: number
  CURRENT_COUNT?: number
}

// ── Layout ──

export type FrontSide = 'auto' | 'top' | 'bottom' | 'left' | 'right'

export interface RackLayout {
  x: number; y: number; w: number; h: number
  config: RackConfig
}

export interface StockerLayout {
  width: number
  height: number
  left: number
  top: number
  vertical: boolean
  flipped: boolean
  along: number
  across: number
  leftRack: RackLayout | null
  rightRack: RackLayout | null
  aisle: { x: number; y: number; w: number; h: number }
  maxBays: number
  bayW: number
}

export function computeLayout(state: any): StockerLayout {
  const { left = 0, top = 0, width = 200, height = 100, aisleRatio = 0.15 } = state
  const frontSide: FrontSide = state.frontSide || 'auto'

  let vertical: boolean
  let flipped: boolean

  switch (frontSide) {
    case 'left': vertical = false; flipped = false; break
    case 'right': vertical = false; flipped = true; break
    case 'top': vertical = true; flipped = false; break
    case 'bottom': vertical = true; flipped = true; break
    default: vertical = height > width; flipped = false; break
  }

  const along = vertical ? height : width
  const across = vertical ? width : height

  const lRack = parseRackConfig(state, 'l')
  const rRack = parseRackConfig(state, 'r')
  const hasLeft = lRack.bays > 0 && lRack.levels > 0
  const hasRight = rRack.bays > 0 && rRack.levels > 0
  const hasBoth = hasLeft && hasRight

  const maxBays = Math.max(lRack.bays || 0, rRack.bays || 0, 1)
  const bayW = along / maxBays

  const aisleSize = across * aisleRatio
  const rackAreaSize = across - aisleSize

  let leftLayout: RackLayout | null = null
  let rightLayout: RackLayout | null = null

  if (vertical) {
    if (hasBoth) {
      const rackW = rackAreaSize / 2
      leftLayout = { x: left, y: top, w: rackW, h: height, config: lRack }
      rightLayout = { x: left + rackW + aisleSize, y: top, w: rackW, h: height, config: rRack }
    } else if (hasLeft) {
      leftLayout = { x: left, y: top, w: rackAreaSize, h: height, config: lRack }
    } else if (hasRight) {
      rightLayout = { x: left + aisleSize, y: top, w: rackAreaSize, h: height, config: rRack }
    }
    const aisleX = leftLayout ? leftLayout.x + leftLayout.w : left
    const aisle = { x: aisleX, y: top, w: aisleSize, h: height }
    return { width, height, left, top, vertical, flipped, along, across, leftRack: leftLayout, rightRack: rightLayout, aisle, maxBays, bayW }
  } else {
    if (hasBoth) {
      const rackH = rackAreaSize / 2
      leftLayout = { x: left, y: top, w: width, h: rackH, config: lRack }
      rightLayout = { x: left, y: top + rackH + aisleSize, w: width, h: rackH, config: rRack }
    } else if (hasLeft) {
      leftLayout = { x: left, y: top, w: width, h: rackAreaSize, config: lRack }
    } else if (hasRight) {
      rightLayout = { x: left, y: top + aisleSize, w: width, h: rackAreaSize, config: rRack }
    }
    const aisleY = leftLayout ? leftLayout.y + leftLayout.h : top
    const aisle = { x: left, y: aisleY, w: width, h: aisleSize }
    return { width, height, left, top, vertical, flipped, along, across, leftRack: leftLayout, rightRack: rightLayout, aisle, maxBays, bayW }
  }
}

export function parseRackConfig(state: any, prefix: 'l' | 'r'): RackConfig {
  return {
    bays: state[`${prefix}Bays`] ?? 10,
    levels: state[`${prefix}Levels`] ?? 5,
    depthCount: state[`${prefix}Depth`] ?? 1
  }
}

// ── Location ──

export const DEFAULT_LOCATION_RULE: LocationRule = {
  pattern: '{side}{bay}-{level}-{depth}',
  sideLabels: { L: 'L', R: 'R' },
  bayDir: 'ltr', levelDir: 'btt',
  bayStart: 1, levelStart: 1, depthStart: 1,
  bayDigits: 2, levelDigits: 2, depthDigits: 0
}

export function parseLocationRule(state: any): LocationRule {
  const raw = state.locationRule
  if (!raw) return DEFAULT_LOCATION_RULE
  if (typeof raw === 'string') {
    try { return { ...DEFAULT_LOCATION_RULE, ...JSON.parse(raw) } } catch { return DEFAULT_LOCATION_RULE }
  }
  return { ...DEFAULT_LOCATION_RULE, ...raw }
}

function padNum(n: number, digits: number): string {
  if (digits <= 0) return String(n)
  return String(n).padStart(digits, '0')
}

export function buildLocationId(
  rule: LocationRule, side: 'L' | 'R', bay: number, level: number, depth: number, rackConfig: RackConfig
): string {
  const sideLabel = rule.sideLabels[side] || side
  const bayNum = rule.bayDir === 'rtl' ? rackConfig.bays - bay + rule.bayStart : bay - 1 + rule.bayStart
  const levelNum = rule.levelDir === 'ttb' ? rackConfig.levels - level + rule.levelStart : level - 1 + rule.levelStart
  const depthNum = depth - 1 + rule.depthStart
  return rule.pattern
    .replace(/{side}/gi, sideLabel)
    .replace(/{bay}/gi, padNum(bayNum, rule.bayDigits))
    .replace(/{level}/gi, padNum(levelNum, rule.levelDigits))
    .replace(/{depth}/gi, padNum(depthNum, rule.depthDigits))
}

// ── NATURE ──

const NATURE: ComponentNature = {
  mutable: false,
  resizable: true,
  rotatable: true,
  properties: [
    { type: 'number', label: 'L-bays', name: 'lBays', property: { min: 0, max: 100, step: 1 } },
    { type: 'number', label: 'L-levels', name: 'lLevels', property: { min: 0, max: 20, step: 1 } },
    { type: 'number', label: 'L-depth', name: 'lDepth', property: { min: 1, max: 3, step: 1 } },
    { type: 'number', label: 'R-bays', name: 'rBays', property: { min: 0, max: 100, step: 1 } },
    { type: 'number', label: 'R-levels', name: 'rLevels', property: { min: 0, max: 20, step: 1 } },
    { type: 'number', label: 'R-depth', name: 'rDepth', property: { min: 1, max: 3, step: 1 } },
    { type: 'number', label: 'cranes', name: 'cranes', property: { min: 1, max: 4, step: 1 } },
    {
      type: 'select', label: 'front-side', name: 'frontSide',
      property: { options: [
        { display: 'Auto', value: 'auto' }, { display: 'Top', value: 'top' },
        { display: 'Bottom', value: 'bottom' }, { display: 'Left', value: 'left' },
        { display: 'Right', value: 'right' }
      ] }
    },
    { type: 'checkbox', label: 'hide-rack-frame', name: 'hideRackFrame' },
    { type: 'number', label: 'aisle-ratio', name: 'aisleRatio', property: { min: 0.05, max: 0.4, step: 0.01 } },
    { type: 'stocker-location', label: 'location-rule', name: 'locationRule' },
    { type: 'number', label: '3d-height', name: 'depth', property: { min: 1 } }
  ],
  help: 'scene/component/stocker'
}

// ── Colors ──

const COLORS = {
  rack: '#e0e0e0',
  rackStroke: '#999',
  aisle: '#d4d4c8',
  aisleRail: '#888',
  crane: '#ff6600',
  craneMast: '#cc5500',
  cellEmpty: '#f5f5f5',
  cellFull: '#4a9eff',
  cellReserved: '#ffcc00',
  cellError: '#e74c3c',
}

// ── Component ──

@sceneComponent('stocker')
export class Stocker extends RectPath(Shape) {
  override get state(): StockerState {
    return super.state as StockerState
  }

  private _parsedCells: StockerCell[] = []
  private _parsedCranes: CraneData[] = []

  get nature() { return NATURE }


  buildRealObject(): RealObject | undefined {
    return new Stocker3d(this)
  }

  get layout(): StockerLayout {
    return computeLayout(this.state)
  }

  get locationRule(): LocationRule {
    return parseLocationRule(this.state)
  }

  get cells(): StockerCell[] {
    return this._parsedCells
  }

  get cranesData(): CraneData[] {
    return this._parsedCranes
  }

  getCellStatus(side: 'L' | 'R', bay: number, level: number, depth: number): string | undefined {
    return this._parsedCells.find(
      c => c.side === side && c.bay === bay && c.level === level && c.depth === depth
    )?.status
  }

  getLocationId(side: 'L' | 'R', bay: number, level: number, depth: number): string {
    const rackConfig = parseRackConfig(this.state, side === 'L' ? 'l' : 'r')
    return buildLocationId(this.locationRule, side, bay, level, depth, rackConfig)
  }

  // ── Data parsing ──

  onchangeData(_after: any, _before: any) {
    const data = (this.data || {}) as StockerData

    if (typeof data.CELLS === 'string') {
      try { this._parsedCells = JSON.parse(data.CELLS) } catch { this._parsedCells = [] }
    } else if (Array.isArray(data.CELLS)) {
      this._parsedCells = data.CELLS
    }

    if (data.CRANES) {
      if (typeof data.CRANES === 'string') {
        try {
          const parsed = JSON.parse(data.CRANES)
          this._parsedCranes = Array.isArray(parsed) ? parsed : []
        } catch { this._parsedCranes = [] }
      } else if (Array.isArray(data.CRANES)) {
        this._parsedCranes = data.CRANES
      }
    } else if (data.CRANE_BAY != null) {
      this._parsedCranes = [{
        id: 'CR1', bay: data.CRANE_BAY, level: data.CRANE_LEVEL ?? 1,
        side: data.CRANE_SIDE, status: data.CRANE_STATUS, carrier: data.CRANE_CARRIER
      }]
    }

    this.invalidate()
  }

  // ── 2D Rendering (top view) ──

  render(ctx: CanvasRenderingContext2D) {
    const { left = 0, top = 0, width = 200, height = 100 } = this.state
    const layout = this.layout

    ctx.beginPath()
    ctx.rect(left, top, width, height)

    if (layout.leftRack) this._drawRack(ctx, layout.leftRack, 'L', layout)
    if (layout.rightRack) this._drawRack(ctx, layout.rightRack, 'R', layout)

    this._drawAisle(ctx, layout)
    this._drawCranes(ctx, layout)
  }

  private _visualBay(layout: StockerLayout, bay: number): number {
    return layout.flipped ? layout.maxBays - bay + 1 : bay
  }

  private _drawRack(ctx: CanvasRenderingContext2D, rack: RackLayout, side: 'L' | 'R', layout: StockerLayout) {
    const { bays, levels, depthCount } = rack.config
    const { vertical, bayW } = layout
    const depthCellSize = vertical ? rack.w / depthCount : rack.h / depthCount

    ctx.strokeStyle = COLORS.rackStroke
    ctx.lineWidth = 0.5

    for (let d = 0; d < depthCount; d++) {
      for (let b = 1; b <= bays; b++) {
        const vb = this._visualBay(layout, b)

        let cx: number, cy: number, cw: number, ch: number
        if (vertical) {
          cx = rack.x + d * depthCellSize
          cy = rack.y + (vb - 1) * bayW
          cw = depthCellSize
          ch = bayW
        } else {
          cx = rack.x + (vb - 1) * bayW
          cy = rack.y + d * depthCellSize
          cw = bayW
          ch = depthCellSize
        }

        let occupied = 0
        for (let l = 1; l <= levels; l++) {
          const s = this.getCellStatus(side, b, l, d + 1)
          if (s === 'FULL' || s === 'RESERVED') occupied++
        }

        const ratio = levels > 0 ? occupied / levels : 0
        ctx.fillStyle = ratio > 0
          ? this._blendColor(COLORS.cellEmpty, COLORS.cellFull, ratio)
          : COLORS.cellEmpty
        ctx.fillRect(cx, cy, cw, ch)
        ctx.strokeRect(cx, cy, cw, ch)
      }
    }
  }

  private _drawAisle(ctx: CanvasRenderingContext2D, layout: StockerLayout) {
    const { aisle, vertical } = layout

    ctx.fillStyle = COLORS.aisle
    ctx.fillRect(aisle.x, aisle.y, aisle.w, aisle.h)

    ctx.strokeStyle = COLORS.aisleRail
    ctx.lineWidth = 2
    ctx.beginPath()

    if (vertical) {
      const railX = aisle.x + aisle.w / 2
      ctx.moveTo(railX, aisle.y)
      ctx.lineTo(railX, aisle.y + aisle.h)
    } else {
      const railY = aisle.y + aisle.h / 2
      ctx.moveTo(aisle.x, railY)
      ctx.lineTo(aisle.x + aisle.w, railY)
    }
    ctx.stroke()
  }

  private _drawCranes(ctx: CanvasRenderingContext2D, layout: StockerLayout) {
    const { cranes: craneCount = 1 } = this.state
    const cranesData = this._parsedCranes
    const { aisle, maxBays, bayW, vertical } = layout

    const count = Math.max(cranesData.length, craneCount)
    const aisleShort = vertical ? aisle.w : aisle.h
    const craneSize = Math.min(bayW, aisleShort) * 0.6

    for (let i = 0; i < count; i++) {
      const cd = cranesData[i]
      const bay = cd?.bay ?? Math.round((i + 1) * maxBays / (count + 1))
      const vb = this._visualBay(layout, bay)

      let cx: number, cy: number
      if (vertical) {
        cx = aisle.x + aisle.w / 2
        cy = aisle.y + (vb - 0.5) * bayW
      } else {
        cx = aisle.x + (vb - 0.5) * bayW
        cy = aisle.y + aisle.h / 2
      }

      ctx.strokeStyle = COLORS.craneMast
      ctx.lineWidth = 2
      ctx.beginPath()
      if (vertical) {
        ctx.moveTo(aisle.x, cy)
        ctx.lineTo(aisle.x + aisle.w, cy)
      } else {
        ctx.moveTo(cx, aisle.y)
        ctx.lineTo(cx, aisle.y + aisle.h)
      }
      ctx.stroke()

      ctx.fillStyle = COLORS.crane
      ctx.fillRect(cx - craneSize / 2, cy - craneSize / 2, craneSize, craneSize)

      const label = cd?.id || `CR${i + 1}`
      ctx.fillStyle = '#fff'
      ctx.font = `${Math.max(craneSize * 0.4, 6)}px sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(label, cx, cy)
    }
  }

  private _blendColor(c1: string, c2: string, t: number): string {
    const parse = (c: string) => {
      const m = c.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i)
      return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [200, 200, 200]
    }
    const [r1, g1, b1] = parse(c1)
    const [r2, g2, b2] = parse(c2)
    const r = Math.round(r1 + (r2 - r1) * t)
    const g = Math.round(g1 + (g2 - g1) * t)
    const b = Math.round(b1 + (b2 - b1) * t)
    return `rgb(${r},${g},${b})`
  }
}

Component.memoize(Stocker.prototype, 'layout', false)
Component.memoize(Stocker.prototype, 'locationRule', false)
